/**
 * AI-CHAT-INSIGHTS suggestion tray — sits between the message list and the
 * input row in the Analyst panel. Renders the register suggestions the
 * conversation surfaced (see `use-insight-suggestions.ts`) as compact cards
 * the user can **Add** or dismiss. Nothing is committed without an explicit
 * Add: the model proposes, a human decides.
 *
 * Chrome mirrors the panel's existing surfaces — true-blue translucent fills,
 * CSS-var typography, Decision Log / Insight Library per-kind iconography.
 */

import {
  Check,
  FlaskConical,
  Lightbulb,
  ListChecks,
  NotebookPen,
  ReceiptText,
  Scale,
  Sparkles,
  Telescope,
  X,
} from "lucide-react";
import type { ChatScope } from "../context-builder";
import type { InsightKind } from "../ai-draft";
import type { SuggestedInsight } from "../use-insight-suggestions";

type Props = {
  suggestions: SuggestedInsight[];
  scanning: boolean;
  /** Conversation scope — drives whether the per-card target chip shows. */
  scope: ChatScope;
  onAccept: (id: string) => void;
  onDismiss: (id: string) => void;
  onDismissAll: () => void;
};

const KIND_META: Record<InsightKind, { label: string; icon: typeof Telescope }> = {
  finding: { label: "Finding", icon: Telescope },
  opportunity: { label: "Opportunity", icon: Lightbulb },
  recommendation: { label: "Recommendation", icon: ReceiptText },
  decision: { label: "Decision", icon: Scale },
  action: { label: "Action", icon: ListChecks },
  assumption: { label: "Assumption", icon: FlaskConical },
  note: { label: "Note", icon: NotebookPen },
};

export function InsightSuggestions({
  suggestions,
  scanning,
  scope,
  onAccept,
  onDismiss,
  onDismissAll,
}: Props) {
  // Dormant until there's something to show. The bare scanning state stays
  // quiet — only surfaces alongside results, as inline header text.
  if (suggestions.length === 0) return null;

  return (
    <section
      aria-label="Suggested register items"
      aria-live="polite"
      className="flex-shrink-0"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "8px 10px",
        borderRadius: 8,
        background: "rgba(var(--accent-true-blue-rgb), 0.05)",
        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.20)",
        maxHeight: 220,
        overflowY: "auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Sparkles
          aria-hidden
          style={{ width: 13, height: 13, color: "var(--accent-true-blue)" }}
        />
        <span
          style={{
            fontSize: "var(--fs-label)",
            fontWeight: 700,
            letterSpacing: "1px",
            textTransform: "uppercase",
            color: "var(--text-secondary)",
          }}
        >
          Suggested for registers
        </span>
        <span style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)", fontWeight: 600 }}>
          ({suggestions.length})
        </span>
        {scanning && (
          <span style={{ fontSize: "var(--fs-micro)", color: "var(--accent-sky)", letterSpacing: 0.5 }}>
            scanning…
          </span>
        )}
        <button
          type="button"
          onClick={onDismissAll}
          aria-label="Dismiss all suggested register items"
          style={{
            marginLeft: "auto",
            background: "none",
            border: 0,
            padding: "1px 4px",
            cursor: "pointer",
            fontSize: "var(--fs-micro)",
            fontWeight: 600,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: "var(--text-muted)",
          }}
        >
          Dismiss all
        </button>
      </div>

      <ul style={{ display: "flex", flexDirection: "column", gap: 6, margin: 0, padding: 0, listStyle: "none" }}>
        {suggestions.map((s) => (
          <SuggestionCard
            key={s.id}
            insight={s}
            showTarget={scope === "MC"}
            onAccept={() => onAccept(s.id)}
            onDismiss={() => onDismiss(s.id)}
          />
        ))}
      </ul>
    </section>
  );
}

function SuggestionCard({
  insight,
  showTarget,
  onAccept,
  onDismiss,
}: {
  insight: SuggestedInsight;
  showTarget: boolean;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const meta = KIND_META[insight.kind];
  const Icon = meta.icon;
  return (
    <li
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "7px 9px",
        borderRadius: 6,
        background: "rgba(var(--accent-true-blue-rgb), 0.04)",
        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Icon aria-hidden style={{ width: 12, height: 12, color: "var(--accent-true-blue)", flexShrink: 0 }} />
        <span
          style={{
            fontSize: "var(--fs-micro)",
            fontWeight: 700,
            letterSpacing: "0.6px",
            textTransform: "uppercase",
            color: "var(--accent-true-blue)",
          }}
        >
          {meta.label}
        </span>
        {showTarget && insight.workshopId && (
          <span
            style={{
              fontSize: "var(--fs-micro)",
              color: "var(--accent-sky)",
              letterSpacing: 0.4,
              background: "rgba(var(--accent-sky-rgb), 0.10)",
              border: "1px solid rgba(var(--accent-sky-rgb), 0.22)",
              borderRadius: 999,
              padding: "0 6px",
              lineHeight: 1.5,
            }}
          >
            {insight.workshopId}
          </span>
        )}
      </div>

      <div style={{ fontSize: "var(--fs-base)", fontWeight: 600, color: "var(--text-primary)" }}>
        {insight.title}
      </div>
      {insight.detail && (
        <div style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", lineHeight: 1.4 }}>
          {insight.detail}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 2 }}>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={`Dismiss suggested ${meta.label.toLowerCase()}: ${insight.title}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            padding: "2px 8px",
            borderRadius: 6,
            background: "transparent",
            border: "1px solid rgba(var(--accent-true-blue-rgb), 0.20)",
            cursor: "pointer",
            fontSize: "var(--fs-micro)",
            fontWeight: 600,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: "var(--text-muted)",
          }}
        >
          <X aria-hidden style={{ width: 11, height: 11 }} />
          Dismiss
        </button>
        <button
          type="button"
          onClick={onAccept}
          aria-label={`Add suggested ${meta.label.toLowerCase()} to register: ${insight.title}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            padding: "2px 8px",
            borderRadius: 6,
            background: "rgba(var(--accent-true-blue-rgb), 0.16)",
            border: "1px solid rgba(var(--accent-true-blue-rgb), 0.40)",
            cursor: "pointer",
            fontSize: "var(--fs-micro)",
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: "var(--accent-sky)",
          }}
        >
          <Check aria-hidden style={{ width: 11, height: 11 }} />
          Add
        </button>
      </div>
    </li>
  );
}
